/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ButtonProps, Theme } from '@coursera/cds-core';
import { Button, breakpoints, useTheme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { StepNames } from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import { Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/program-home';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const navigationControls = (theme: Theme) =>
  css({
    display: 'flex',
    gap: '20px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  });

const navigationButtons = (theme: Theme) =>
  css({
    display: 'flex',
    gap: '12px',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    [breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  });

const buttonStyles = (theme: Theme) =>
  css({
    [breakpoints.down('xs')]: {
      width: '100%',
    },
  });

export type PropsFromCaller = {
  currentStepIdx: number;
  onContinue: () => void;
  onBack: () => void;
  canContinue?: boolean;
  onSkip: () => void;
  onCompleteClick: () => void;
  isFinalStep: boolean;
  hasCompletedOnboarding: boolean;
};

export const NavigationControls = ({
  currentStepIdx,
  onContinue,
  onBack,
  canContinue,
  onSkip,
  onCompleteClick,
  isFinalStep,
  hasCompletedOnboarding,
}: PropsFromCaller) => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <div css={navigationControls(theme)} data-testid="navigation-controls">
        <div css={navigationButtons(theme)}>
          {isFinalStep ? (
            <TrackedButton
              {...Button.defaultProps}
              trackingName="complete_button"
              size="medium"
              variant="primary"
              onClick={onCompleteClick}
              css={buttonStyles}
              disabled={!canContinue}
            >
              {_t('Complete')}
            </TrackedButton>
          ) : (
            <TrackedButton
              {...Button.defaultProps}
              trackingName="continue_button"
              size="medium"
              variant="primary"
              icon={<ArrowNextIcon />}
              iconPosition="after"
              onClick={onContinue}
              css={buttonStyles}
              disabled={!canContinue}
            >
              {_t('Continue')}
            </TrackedButton>
          )}
          {currentStepIdx !== 0 && (
            <TrackedButton
              {...Button.defaultProps}
              trackingName="back_button"
              size="medium"
              variant="secondary"
              onClick={onBack}
              css={buttonStyles}
            >
              {_t('Back')}
            </TrackedButton>
          )}
        </div>
        <TrackedButton
          {...Button.defaultProps}
          trackingName={hasCompletedOnboarding ? 'cancel' : 'remind_button'}
          data-e2e="onboarding-remind-cancel-button"
          trackingData={{
            currentStep: StepNames[currentStepIdx],
          }}
          variant="ghost"
          size="medium"
          onClick={onSkip}
          css={buttonStyles}
        >
          {hasCompletedOnboarding ? _t('Cancel') : _t('Remind me later')}
        </TrackedButton>
      </div>
      <Spacer size={48} />
    </React.Fragment>
  );
};
